import React, { useState, useEffect, useRef, useCallback } from "react";
import { Search, RefreshCcw } from "lucide-react";
import { Link } from "react-router-dom";
import CategorySelect from "../../../components/selectCategory/SelectCategory";
import ItemCard from "../../../components/ItemCard/ItemCard";
import ProductLoadingSkeleton from "./ProductSkelton";

const AllProducts = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hasMore, setHasMore] = useState(true);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [limit, setLimit] = useState(20);

  // Filter state
  const [categoryId, setCategoryId] = useState(1);
  const [sort, setSort] = useState(2);

  // Reference to observe the last product element
  const observer = useRef();
  const lastProductElementRef = useCallback(
    (node) => {
      if (loading) return;
      if (observer.current) observer.current.disconnect();
      
      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasMore) {
          setCurrentPage((prevPage) => prevPage + 1);
        }
      });
      
      if (node) observer.current.observe(node);
    },
    [loading, hasMore]
  );

  const fetchProducts = async (isInitialLoad = false) => {
    setLoading(true);
    try {
      const response = await fetch(
        `https://apimedtools.vercel.app/api/products?categoryId=${categoryId}&page=${currentPage}&limit=${limit}&sort=${sort}`
      );

      if (!response.ok) {
        throw new Error("Failed to fetch products");
      }

      const data = await response.json();
      const newProducts = data.products || [];
      
      if (isInitialLoad) {
        setProducts(newProducts);
      } else {
        setProducts((prevProducts) => [...prevProducts, ...newProducts]);
      }
      
      setHasMore(newProducts.length > 0 && newProducts.length === limit);
      setLoading(false);
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };

  // Initial load and when filters change
  useEffect(() => {
    setProducts([]);
    setCurrentPage(1);
    setHasMore(true);
    fetchProducts(true);
  }, [categoryId, sort, limit]);

  // Load more when currentPage changes (triggered by intersection observer)
  useEffect(() => {
    if (currentPage > 1) {
      fetchProducts();
    }
  }, [currentPage]);

  const handleCategoryChange = (newCategoryId) => {
    setCategoryId(newCategoryId);
  };

  const handleSortChange = (e) => {
    setSort(parseInt(e.target.value));
  };

  const handleLimitChange = (e) => {
    setLimit(parseInt(e.target.value));
  };

  const handleRefresh = () => {
    setProducts([]);
    setCurrentPage(1);
    setHasMore(true);
    fetchProducts(true);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 py-6 sm:px-6 lg:px-8 flex justify-between items-center">
          <h1 className="text-3xl font-bold text-gray-900">Product Catalog</h1>
          <button
            onClick={handleRefresh}
            className="flex items-center px-4 py-2 text-sm font-medium text-white bg-_blue rounded-md hover:bg-blue-700"
          >
            <RefreshCcw className="mr-2 h-4 w-4" />
            Refresh
          </button>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Filters */}
        <div className="bg-white p-6 rounded-lg shadow mb-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <label
                htmlFor="category"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Category
              </label>
              <CategorySelect onCategorySelect={handleCategoryChange} initialCategoryId={1} />
            </div>

            <div>
              <label
                htmlFor="sort"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Sort By
              </label>
              <select
                id="sort"
                value={sort}
                onChange={handleSortChange}
                className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              >
                <option value={1}>Best Match</option>
                <option value={2}>Price: Low to High</option>
                <option value={3}>Price: High to Low</option>
                <option value={4}>Newest First</option>
              </select>
            </div>

            <div>
              <label
                htmlFor="limit"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Products Per Page
              </label>
              <select
                id="limit"
                value={limit}
                onChange={handleLimitChange}
                className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              >
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
              </select>
            </div>
          </div>
        </div>

        {/* Products Grid */}
        {error ? (
          <div className="bg-red-50 p-4 rounded-md">
            <div className="flex">
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">
                  Error loading products
                </h3>
                <div className="mt-2 text-sm text-red-700">
                  <p>{error}</p>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <>
            {products.length === 0 && !loading ? (
              <div className="text-center py-12">
                <Search className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900">
                  No products found
                </h3>
                <p className="mt-1 text-sm text-gray-500">
                  Try changing your search criteria or category.
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {products.map((product, index) => (
                    <Link
                    to={`/admin/product/edit/${product.mainProductId}`}
                    key={product.mainProductId}
                    className=""
                  >
                    <ItemCard product={product} showButton = {true} text = {"Edit"} bgColor="_blue" />
                  </Link>
                ))}
              </div>
            )}

            {/* Loading indicator */}
            {loading && (
             <ProductLoadingSkeleton/>
            )}
            
            {/* End of results message */}
            {!loading && !hasMore && products.length > 0 && (
              <div className="text-center mt-8 pb-8 text-gray-500">
                No more products to load
              </div>
            )}
          </>
        )}
      </main>
    </div>
  );
};

export default AllProducts;